import React, { useCallback, useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Share2, UserPlus, Mail, CheckCircle2, X, Send, Loader2, Award, GitBranch } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function CareCircle() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [pendingSent, setPendingSent] = useState([]);
  const [pendingRecv, setPendingRecv] = useState([]);
  const [lineage, setLineage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, sent, recv, lin] = await Promise.all([
        api.get("/care-circle/members"),
        api.get("/care-circle/invites", { params: { direction: "sent" } }),
        api.get("/care-circle/invites", { params: { direction: "received" } }),
        user?.id ? api.get(`/care-circle/lineage/${user.id}`) : Promise.resolve({ data: { lineage: [] } }),
      ]);
      setMembers(m.data || []);
      setPendingSent(sent.data || []);
      setPendingRecv(recv.data || []);
      setLineage(lin.data?.lineage || []);
    } catch { toast.error("Failed to load care circle"); }
    finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const accept = async (i) => {
    setBusyId(i.id);
    try {
      await api.post(`/care-circle/invites/${i.id}/accept`);
      toast.success(`Joined ${i.inviter_name || "the"} care circle`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const decline = async (i) => {
    setBusyId(i.id);
    try {
      await api.post(`/care-circle/invites/${i.id}/decline`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#8A9992]">Your network</div>
          <h1 className="text-3xl font-semibold text-[#1F2A24] mt-1" style={{ fontFamily: "Instrument Sans" }}>Care Circle</h1>
          <p className="text-sm text-[#6C7B72] mt-1 max-w-xl">Trusted practitioners you can refer patients to and share consultation history with (with patient consent).</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-2xl bg-[#E6F0EC] px-4 py-2 text-sm text-[#0A4C36] font-semibold inline-flex items-center gap-2" title="Referral credits">
            <Award size={14} /> {user?.care_credits ?? 0} credits
          </div>
          <button onClick={() => setAddOpen(true)} className="btn-emerald" data-testid="cc-add-btn">
            <UserPlus size={14} /> Add member
          </button>
        </div>
      </div>

      {/* Pending received (must be handled first) */}
      {pendingRecv.length > 0 && (
        <section className="card-soft p-6 mb-6 border-l-4 border-[#8A5A34] bg-[#FCE7D9]/40" data-testid="cc-pending-recv">
          <div className="field-label mb-3 text-[#8A5A34]">Pending action ({pendingRecv.length})</div>
          <div className="space-y-3">
            {pendingRecv.map((i) => (
              <div key={i.id} className="flex items-center gap-3 flex-wrap" data-testid={`cc-recv-${i.id}`}>
                <div className="w-10 h-10 rounded-full bg-[#0A4C36]/10 text-[#0A4C36] font-semibold flex items-center justify-center text-xs">
                  {(i.inviter_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1F2A24]">{i.inviter_name} invited you to their care circle</div>
                  {i.message && <div className="text-xs text-[#5C7568] mt-0.5">&ldquo;{i.message}&rdquo;</div>}
                </div>
                <button onClick={() => accept(i)} disabled={busyId === i.id} className="btn-emerald" data-testid={`cc-accept-${i.id}`}>
                  {busyId === i.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Accept
                </button>
                <button onClick={() => decline(i)} disabled={busyId === i.id} className="btn-outline-emerald" data-testid={`cc-decline-${i.id}`}>
                  <X size={13} /> Decline
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Members grid */}
      <section className="card-soft p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="field-label">Members {members.length > 0 && <span className="text-[#8A9992] font-normal">· {members.length}</span>}</div>
        </div>
        {loading ? (
          <div className="py-8 text-center text-xs text-[#8A9992]"><Loader2 size={14} className="animate-spin inline mr-2" />Loading…</div>
        ) : members.length === 0 ? (
          <div className="py-10 text-center text-sm text-[#8A9992] bg-[#F7F4EC]/60 rounded-2xl">
            Nobody in your care circle yet. Add a colleague using the button above.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {members.map((m) => (
              <div key={m.id} className="rounded-2xl bg-[#F7F4EC]/70 border border-[#EFECE5] p-4" data-testid={`cc-member-${m.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0A4C36] text-white font-semibold flex items-center justify-center text-xs">
                    {(m.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#1F2A24] truncate">{m.name}</div>
                    <div className="text-[11px] text-[#8A9992] truncate">{m.email}</div>
                  </div>
                </div>
                {(m.specialties || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {m.specialties.slice(0, 3).map((s, i) => (
                      <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#E6F0EC] text-[#0A4C36]">{s}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Sent invites */}
      {pendingSent.length > 0 && (
        <section className="card-soft p-6 mb-6" data-testid="cc-pending-sent">
          <div className="field-label mb-3">Invites you sent ({pendingSent.length})</div>
          <div className="space-y-2">
            {pendingSent.map((i) => (
              <div key={i.id} className="flex items-center gap-3 py-2 border-b border-[#EFECE5] last:border-b-0">
                <Mail size={14} className="text-[#8A9992]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#1F2A24] truncate">{i.invitee_name || i.invitee_email}</div>
                  <div className="text-[11px] text-[#8A9992]">{i.invitee_email}</div>
                </div>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FCE7D9] text-[#8A5A34]">
                  {i.status === "signed_up" ? "Signed up · awaiting accept" : i.status}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Lineage */}
      {lineage.length > 0 && (
        <section className="card-soft p-6" data-testid="cc-lineage">
          <div className="field-label mb-3 inline-flex items-center gap-2"><GitBranch size={12} /> Your referral lineage</div>
          <div className="flex flex-wrap items-center gap-2">
            {lineage.map((d, i) => (
              <React.Fragment key={d.id}>
                <div className="rounded-full bg-[#E6F0EC] px-3 py-1.5 text-sm text-[#0A4C36] font-semibold">{d.name}</div>
                {i < lineage.length - 1 && <span className="text-[#8A9992]">→</span>}
              </React.Fragment>
            ))}
            <span className="text-[#8A9992]">→</span>
            <div className="rounded-full bg-[#0A4C36] text-white px-3 py-1.5 text-sm font-semibold">You</div>
          </div>
        </section>
      )}

      <AddMemberDialog open={addOpen} onOpenChange={setAddOpen} onDone={load} />
    </Layout>
  );
}

function AddMemberDialog({ open, onOpenChange, onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("doctor");
  const [speciality, setSpeciality] = useState("");
  const [message, setMessage] = useState("");
  const [phase, setPhase] = useState("input"); // input | looked_up | busy
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setEmail(""); setName(""); setRole("doctor"); setSpeciality(""); setMessage("");
    setPhase("input"); setFound(null); setBusy(false);
  };
  useEffect(() => { if (!open) reset(); }, [open]);

  const lookup = async () => {
    if (!email) { toast.error("Enter an email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/care-circle/lookup", { email });
      setFound(data);
      setPhase("looked_up");
      if (data.on_hc && data.doctor && !name) setName(data.doctor.name || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Lookup failed"); }
    finally { setBusy(false); }
  };

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/care-circle/invite", { email, name, role, speciality, message });
      if (data.status === "invited_existing") toast.success("Invite sent — they'll see it in their notifications");
      else toast.success("Invite email sent");
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[24px] max-w-md" data-testid="cc-add-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Add to care circle</DialogTitle>
          <DialogDescription className="text-xs text-[#6C7B72]">Enter their email — we'll check if they're already on Health Circles.</DialogDescription>
        </DialogHeader>

        {phase === "input" && (
          <div className="space-y-3">
            <label className="block">
              <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Email</div>
              <input value={email} onChange={(e) => setEmail(e.target.value)} className="input-clean" placeholder="colleague@hospital.org" data-testid="cc-add-email" />
            </label>
            <div className="pt-3 flex justify-end gap-2">
              <button onClick={() => onOpenChange(false)} className="btn-outline-emerald">Cancel</button>
              <button onClick={lookup} disabled={busy || !email} className="btn-emerald" data-testid="cc-add-lookup">
                {busy ? <Loader2 size={13} className="animate-spin" /> : null} Continue
              </button>
            </div>
          </div>
        )}

        {phase === "looked_up" && (
          <div className="space-y-3">
            {found?.self && (
              <div className="p-3 rounded-2xl bg-[#F8E7DF] border border-[#B14A2A]/30 text-sm text-[#5C3A21]">That's your own email :)</div>
            )}
            {found?.already_linked && (
              <div className="p-3 rounded-2xl bg-[#FCE7D9] border border-[#8A5A34]/30 text-sm text-[#5C3A21]">
                <b>{found.doctor?.name}</b> is already in your care circle.
              </div>
            )}
            {!found?.self && !found?.already_linked && found?.on_hc && found?.doctor && (
              <>
                <div className="p-3 rounded-2xl bg-[#E6F0EC] border border-[#0A4C36]/20" data-testid="cc-found-on-hc">
                  <div className="text-[10px] uppercase tracking-widest text-[#0A4C36] mb-1">Found on Health Circles</div>
                  <div className="font-semibold text-[#1F2A24]">{found.doctor.name}</div>
                  <div className="text-xs text-[#5C7568]">{found.doctor.email}</div>
                  {(found.doctor.specialties || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {found.doctor.specialties.map((s, i) => <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white text-[#0A4C36]">{s}</span>)}
                    </div>
                  )}
                </div>
                <label className="block">
                  <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Optional note</div>
                  <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} className="input-clean" placeholder="Hey — would you like to be in my care circle?" />
                </label>
              </>
            )}
            {!found?.on_hc && (
              <>
                <div className="p-3 rounded-2xl bg-[#FCE7D9]/60 border border-[#8A5A34]/20" data-testid="cc-not-on-hc">
                  <div className="text-sm text-[#5C3A21]">
                    <b>{email}</b> is not yet on Health Circles. Would you like to invite them to join <b>and</b> add them to your care circle? They'll get an email with your unique referral link.
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Their name</div>
                    <input value={name} onChange={(e) => setName(e.target.value)} className="input-clean" placeholder="Dr. Jane Doe" />
                  </label>
                  <label className="block">
                    <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Role</div>
                    <select value={role} onChange={(e) => setRole(e.target.value)} className="input-clean">
                      <option value="doctor">Doctor</option>
                      <option value="physiotherapist">Physiotherapist</option>
                      <option value="nurse">Nurse</option>
                      <option value="pharmacist">Pharmacist</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                </div>
                <label className="block">
                  <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Speciality (optional)</div>
                  <input value={speciality} onChange={(e) => setSpeciality(e.target.value)} className="input-clean" placeholder="Cardiology" />
                </label>
                <label className="block">
                  <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Personal note (optional)</div>
                  <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} className="input-clean" placeholder="Hey — I'd love to have you in my care circle on HC…" />
                </label>
              </>
            )}
            <div className="pt-3 flex justify-end gap-2">
              <button onClick={() => setPhase("input")} className="btn-outline-emerald">Back</button>
              {!found?.self && !found?.already_linked && (
                <button onClick={invite} disabled={busy} className="btn-emerald" data-testid="cc-send-invite">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {found?.on_hc ? "Send invite" : "Invite to Health Circles"}
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
